import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { Alert, Flex, AlertTitle, Spacer, CloseButton, Avatar, Skeleton, Text, Box, Portal, SkeletonText, List, ListItem, ListIcon, HStack, chakra, Link, VStack, Button, Popover, PopoverTrigger, PopoverContent, PopoverHeader, PopoverBody, useDisclosure, Heading, Checkbox, RadioGroup, Stack, Radio, Divider, extendTheme } from "@chakra-ui/react";
import { Info, Cancel, Check, CircleSolid, Circle } from "@airhelp/icons";
import { cloneElement, useRef, createContext, useContext, useMemo, useEffect, useState } from "react";
import { AirhelpPlus, Airhelp, InfoBox, USAToday, WSJ, TheWashingtonPost, EURegulation, ConsumerRights, TrustpilotStars, LanguageSwitcherModal, Input, Select, LinkGenerator, theme } from "@airhelp/react";
import { u as useFunnel } from "./react-D5l6P7RL.js";
import { F, R, S } from "./react-D5l6P7RL.js";
import { FormProvider, useController, useForm as useForm$1 } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import z from "zod";
import { branch, buildSteps, group, split, step, stepTemplate } from "@airhelp/funnel-core";
const alertThemes = {
  info: {
    background: "primary.100",
    borderColor: "primary.200",
    iconColor: "primary.500",
    textColor: "primary.900"
  },
  success: {
    background: "success.100",
    borderColor: "success.200",
    iconColor: "success.500",
    textColor: "primary.900"
  },
  danger: {
    background: "danger.100",
    borderColor: "danger.200",
    iconColor: "danger.500",
    textColor: "primary.900"
  }
};
function FunnelAlert({
  children,
  variant = "info",
  isOpen = true,
  onClose,
  icon = /* @__PURE__ */ jsx(Info, {}),
  ...props
}) {
  const styles = alertThemes[variant];
  const styledIcon = cloneElement(icon, {
    mr: 3,
    boxSize: "20px",
    color: styles.iconColor
  });
  if (!isOpen) return null;
  return /* @__PURE__ */ jsx(
    Alert,
    {
      background: styles.background,
      borderBottom: "1px",
      borderColor: styles.borderColor,
      ...props,
      children: /* @__PURE__ */ jsx(
        Flex,
        {
          alignItems: { base: "left", md: "center" },
          direction: { base: "column", md: "row" },
          justifyContent: "space-between",
          margin: "0 auto",
          width: "100%",
          children: /* @__PURE__ */ jsxs(
            Flex,
            {
              alignItems: "center",
              maxW: { base: "calc(100vw - 16px)", md: "768px", xl: "1200px" },
              mx: "auto",
              w: "full",
              children: [
                styledIcon,
                /* @__PURE__ */ jsx(AlertTitle, { color: styles.textColor, fontWeight: "medium", children }),
                /* @__PURE__ */ jsx(Spacer, {}),
                onClose ? /* @__PURE__ */ jsx(
                  CloseButton,
                  {
                    "aria-label": "Close notification",
                    color: "greyscale.600",
                    "data-testid": "returning-users-close-mobile",
                    onClick: onClose,
                    right: -1,
                    top: -1
                  }
                ) : null
              ]
            }
          )
        }
      )
    }
  );
}
function FunnelAvatar({
  name,
  role,
  image,
  isSkeletonShown
}) {
  return /* @__PURE__ */ jsxs(Flex, { "data-testid": "funnelAvatar", mb: 6, children: [
    /* @__PURE__ */ jsx(Avatar, { bg: "transparent", boxSize: 12, mr: 4, src: image }),
    /* @__PURE__ */ jsxs(Flex, { direction: "column", justify: "center", children: [
      /* @__PURE__ */ jsx(Skeleton, { isLoaded: !isSkeletonShown, children: /* @__PURE__ */ jsx(Text, { fontWeight: "bold", variant: "medium", children: name }) }),
      /* @__PURE__ */ jsx(Skeleton, { isLoaded: !isSkeletonShown, children: /* @__PURE__ */ jsx(Text, { fontSize: "sm", children: role }) })
    ] })
  ] });
}
const getLogo = (variant) => {
  const Component = variant === "ahplus" ? AirhelpPlus : Airhelp;
  const widthProps = variant === "ahplus" ? { base: "110px", xl: "150px" } : { base: "94px", xl: "150px" };
  return /* @__PURE__ */ jsx(
    Component,
    {
      color: "primary.900",
      width: widthProps,
      mt: { base: variant === "ahplus" ? "6px" : "-2px", xl: "unset" },
      height: { base: "32px", xl: "auto" },
      alignSelf: "center"
    }
  );
};
function AirhelpLogo({
  variant = "ahplus",
  href = "https://www.airhelp.com/",
  target = "_blank",
  disabledLink
}) {
  if (disabledLink) {
    return /* @__PURE__ */ jsx(Fragment, { children: getLogo(variant) });
  }
  return /* @__PURE__ */ jsx(
    "a",
    {
      href,
      rel: "noopener noreferrer",
      target,
      style: {
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center"
      },
      children: getLogo(variant)
    }
  );
}
function Caption({
  bold,
  danger,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    Text,
    {
      color: danger ? "danger.700" : "primary",
      fontSize: "sm",
      fontWeight: bold ? "medium" : "regular",
      sx: {
        "& a,a:visited": {
          color: "primary.500",
          textDecoration: "none"
        }
      },
      ...props
    }
  );
}
function Note({
  children,
  icon = /* @__PURE__ */ jsx(Info, {}),
  variant = "secondary"
}) {
  const colouredIcon = cloneElement(icon, {
    color: "primary.500"
  });
  return /* @__PURE__ */ jsx(InfoBox, { icon: colouredIcon, isChat: true, variant, children });
}
function ErrorNote({
  children,
  icon = /* @__PURE__ */ jsx(Cancel, {})
}) {
  const colouredIcon = cloneElement(icon, {
    color: "danger.500"
  });
  return /* @__PURE__ */ jsx(InfoBox, { icon: colouredIcon, isChat: true, variant: "danger", children });
}
function Badges({
  variant,
  title
}) {
  const color = variant === "ota-social-proof" ? "greyscale.500" : "greyscale.100";
  const titleWeight = variant === "ota-social-proof" ? "normal" : "bold";
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(Text, { color, fontSize: "sm", fontWeight: titleWeight, children: title }),
    /* @__PURE__ */ jsxs(Flex, { mt: 8, children: [
      /* @__PURE__ */ jsx(USAToday, { color, height: "30px", mr: 10, width: "80px" }),
      /* @__PURE__ */ jsx(WSJ, { color, height: "30px", width: "57px" })
    ] }),
    /* @__PURE__ */ jsx(Flex, { mt: 8, children: /* @__PURE__ */ jsx(TheWashingtonPost, { color, height: "30px", width: "190px" }) })
  ] });
}
function FunnelSidebar({
  content,
  featuredInTitle,
  logoVariant = "ahplus",
  logoHref,
  logoTarget,
  disabledLink,
  header
}) {
  return /* @__PURE__ */ jsx(
    Box,
    {
      backgroundColor: "greyscale.300",
      display: { base: "none", xl: "flex" },
      flex: 2,
      justifyContent: "flex-end",
      minW: "440px",
      children: /* @__PURE__ */ jsxs(Flex, { direction: "column", height: "100%", position: "relative", w: "440px", children: [
        /* @__PURE__ */ jsxs(Box, { flex: "1 0 auto", m: "48px 64px 32px", children: [
          header,
          /* @__PURE__ */ jsx(Box, { children: /* @__PURE__ */ jsx(
            AirhelpLogo,
            {
              disabledLink,
              href: logoHref,
              variant: logoVariant,
              target: logoTarget
            }
          ) }),
          content
        ] }),
        /* @__PURE__ */ jsx(Box, { alignSelf: "flex-end", children: /* @__PURE__ */ jsxs(
          "svg",
          {
            fill: "none",
            viewBox: "0 0 565 474",
            width: "576",
            xmlns: "http://www.w3.org/2000/svg",
            children: [
              /* @__PURE__ */ jsx(
                "mask",
                {
                  height: "474",
                  id: "mask0_2929_15436",
                  maskUnits: "userSpaceOnUse",
                  style: { maskType: "alpha" },
                  width: "565",
                  x: "0",
                  y: "0",
                  children: /* @__PURE__ */ jsx(
                    "rect",
                    {
                      fill: "#F3F6F9",
                      height: "473.976",
                      width: "564.618",
                      x: "0.00488281",
                      y: "0.024353"
                    }
                  )
                }
              ),
              /* @__PURE__ */ jsxs("g", { mask: "url(#mask0_2929_15436)", children: [
                /* @__PURE__ */ jsx(
                  "path",
                  {
                    d: "M341.996 219.466C374.759 134.022 495.523 133.684 528.764 218.944L697.631 652.073C717.082 701.964 680.284 755.806 626.736 755.806H247.012C193.645 755.806 156.857 702.3 175.964 652.47L341.996 219.466Z",
                    fill: "#FF6B73"
                  }
                ),
                /* @__PURE__ */ jsx(
                  "path",
                  {
                    d: "M157.771 64.2758C190.534 -21.1687 311.299 -21.5066 344.539 63.7532L513.406 496.882C532.858 546.773 496.06 600.615 442.511 600.615H62.7877C9.42051 600.615 -27.3678 547.109 -8.26099 497.279L157.771 64.2758Z",
                    fill: "#0052BA"
                  }
                ),
                /* @__PURE__ */ jsx(
                  "path",
                  {
                    clipRule: "evenodd",
                    d: "M385.871 167.914L513.973 496.484C533.424 546.375 496.626 600.217 443.078 600.217H196.413L342.562 219.067C351.567 195.585 367.217 178.53 385.871 167.914Z",
                    fill: "#0D3073",
                    fillRule: "evenodd"
                  }
                )
              ] })
            ]
          }
        ) }),
        /* @__PURE__ */ jsx(
          Flex,
          {
            bottom: "64px",
            direction: "column",
            mx: "64px",
            my: 0,
            position: "absolute",
            sx: {
              "@media(height<730px)": { bottom: "32px" },
              "@media(height<700px)": { display: "none" }
            },
            zIndex: 10,
            children: /* @__PURE__ */ jsx(Badges, { title: featuredInTitle })
          }
        )
      ] })
    }
  );
}
const AlertContext = createContext({
  ref: void 0
});
function FunnelShell({
  children,
  sidebarContent,
  logoHref = "https://www.airhelp.com/",
  disabledLink,
  logoVariant = "base",
  logoTarget,
  header,
  ...props
}) {
  const alertRef = useRef(null);
  return /* @__PURE__ */ jsx(Flex, { minHeight: "100vh", children: /* @__PURE__ */ jsxs(AlertContext.Provider, { value: { ref: alertRef }, children: [
    /* @__PURE__ */ jsx(
      FunnelSidebar,
      {
        content: sidebarContent,
        disabledLink,
        featuredInTitle: "Featured in",
        header,
        logoHref,
        logoVariant,
        logoTarget
      }
    ),
    /* @__PURE__ */ jsxs(Flex, { direction: "column", flex: 5, ...props, children: [
      /* @__PURE__ */ jsx(Box, { "data-testid": "alert-portal", ref: alertRef }),
      children
    ] })
  ] }) });
}
function FunnelAlertPortal({ children }) {
  const ref = useFunnelAlertRef();
  return /* @__PURE__ */ jsx(Portal, { containerRef: ref, children });
}
function useFunnelAlertRef() {
  return useContext(AlertContext).ref;
}
function FunnelHeader({
  badgeText,
  badgeVariant = "ec261",
  logoVariant = "base",
  logoHref,
  logoTarget,
  disabledLink,
  stretch = false,
  settingSwitcher,
  isSkeletonShown,
  headerStart,
  ...props
}) {
  return /* @__PURE__ */ jsxs(Box, { ...props, children: [
    /* @__PURE__ */ jsx(
      Box,
      {
        h: { base: 14, sm: 16 },
        m: {
          base: "0 16px",
          md: "0 auto",
          xl: stretch ? "0 16px 0 auto" : "0"
        },
        maxW: "720px",
        minW: { base: "auto", md: "720px" },
        children: /* @__PURE__ */ jsxs(
          Flex,
          {
            h: "100%",
            justifyContent: { base: "space-between", xl: "flex-end" },
            w: "100%",
            children: [
              /* @__PURE__ */ jsxs(
                Box,
                {
                  display: { base: "flex", xl: "none" },
                  flexShrink: 0,
                  height: { base: "34px", sm: 10 },
                  alignSelf: "center",
                  children: [
                    headerStart,
                    /* @__PURE__ */ jsx(
                      AirhelpLogo,
                      {
                        disabledLink,
                        href: logoHref,
                        target: logoTarget,
                        variant: logoVariant
                      }
                    )
                  ]
                }
              ),
              /* @__PURE__ */ jsxs(Flex, { alignItems: "center", justifyContent: "flex-end", children: [
                isSkeletonShown ? /* @__PURE__ */ jsx(
                  SkeletonText,
                  {
                    display: { base: "none", md: "flex" },
                    noOfLines: 1,
                    skeletonHeight: 5,
                    w: 300
                  }
                ) : /* @__PURE__ */ jsx(
                  Badge,
                  {
                    background: "transparent!important",
                    display: { base: "none", md: "flex" },
                    mr: { base: 0, md: "12" },
                    text: badgeText,
                    variant: badgeVariant,
                    w: "fit-content"
                  }
                ),
                /* @__PURE__ */ jsx(
                  Flex,
                  {
                    alignItems: "center",
                    justifyContent: "space-between",
                    mr: { base: 0, md: 4, xl: 0 },
                    children: settingSwitcher
                  }
                )
              ] })
            ]
          }
        )
      }
    ),
    /* @__PURE__ */ jsx(
      Skeleton,
      {
        display: { base: "flex", md: "none" },
        isLoaded: !isSkeletonShown,
        children: /* @__PURE__ */ jsx(
          Badge,
          {
            borderRadius: 0,
            display: { base: "flex", md: "none" },
            text: badgeText,
            variant: badgeVariant
          }
        )
      }
    )
  ] });
}
function Badge({
  text,
  variant = "ec261",
  ...props
}) {
  return variant === "ec261" ? /* @__PURE__ */ jsx(EURegulation, { text, ...props }) : /* @__PURE__ */ jsx(ConsumerRights, { text, ...props });
}
function FunnelProgressBar({
  steps,
  activeStep
}) {
  return /* @__PURE__ */ jsx(List, { mt: 16, children: steps.map((step2, idx) => {
    return /* @__PURE__ */ jsx(
      ProgressBarItem,
      {
        isActive: idx === activeStep,
        isInactive: idx > activeStep,
        isVisited: idx < activeStep,
        title: step2
      },
      idx
    );
  }) });
}
function ProgressBarItem({
  title,
  isVisited,
  isActive,
  isInactive
}) {
  if (isVisited) {
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsxs(
        ListItem,
        {
          borderLeftColor: "greyscale.400",
          borderLeftStyle: "solid",
          borderLeftWidth: "2px",
          lineHeight: "24px",
          px: 9,
          py: 1,
          children: [
            /* @__PURE__ */ jsx(
              ListIcon,
              {
                as: Check,
                boxSize: 6,
                color: "font.primary.900",
                mr: 2
              }
            ),
            /* @__PURE__ */ jsx(
              Text,
              {
                as: "span",
                color: "font.primary.900",
                fontSize: "md",
                verticalAlign: "text-bottom",
                children: title
              }
            )
          ]
        }
      ),
      /* @__PURE__ */ jsx(ProgressBarSpacer, {})
    ] });
  }
  if (isActive) {
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsxs(
        ListItem,
        {
          borderLeftColor: "primary.500",
          borderLeftStyle: "solid",
          borderLeftWidth: "2px",
          lineHeight: "24px",
          px: 9,
          py: 1,
          children: [
            /* @__PURE__ */ jsx(ListIcon, { as: CircleSolid, boxSize: 5, mr: 3 }),
            /* @__PURE__ */ jsx(
              Text,
              {
                as: "span",
                color: "font.primary.900",
                fontSize: "md",
                fontWeight: 500,
                children: title
              }
            )
          ]
        }
      ),
      /* @__PURE__ */ jsx(ProgressBarSpacer, {})
    ] });
  }
  if (isInactive) {
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsxs(
        ListItem,
        {
          borderLeftColor: "greyscale.400",
          borderLeftStyle: "solid",
          borderLeftWidth: "2px",
          lineHeight: "24px",
          px: 9,
          py: 1,
          children: [
            /* @__PURE__ */ jsx(
              ListIcon,
              {
                as: Circle,
                boxSize: 5,
                color: "greyscale.500",
                mr: 3
              }
            ),
            /* @__PURE__ */ jsx(Text, { as: "span", color: "greyscale.500", fontSize: "md", children: title })
          ]
        }
      ),
      /* @__PURE__ */ jsx(ProgressBarSpacer, {})
    ] });
  }
  return null;
}
function ProgressBarSpacer() {
  return /* @__PURE__ */ jsx(
    Spacer,
    {
      _last: { display: "none" },
      borderLeftColor: "greyscale.400",
      borderLeftStyle: "solid",
      borderLeftWidth: "2px",
      py: 3
    }
  );
}
function FunnelContent({
  children,
  avatar,
  header,
  footer,
  ...props
}) {
  return /* @__PURE__ */ jsxs(
    Flex,
    {
      alignItems: { base: "center", xl: "flex-start" },
      direction: "column",
      flex: 5,
      justifyContent: "space-between",
      w: "100%",
      ...props,
      children: [
        /* @__PURE__ */ jsxs(
          Box,
          {
            flex: "1 0 auto",
            maxWidth: "704px",
            mb: "10",
            ml: { base: 0, xl: "12%" },
            w: "100%",
            children: [
              header,
              /* @__PURE__ */ jsx(Box, { m: { base: "24px 16px", md: "24px 0" }, mt: { md: 12 }, children: avatar }),
              /* @__PURE__ */ jsx(Box, { flex: 1, m: { base: "0 16px", md: "0" }, children })
            ]
          }
        ),
        footer
      ]
    }
  );
}
function FunnelFooter({
  isSkeletonShown,
  links = [],
  copyright,
  trustpilotText,
  trustpilotStars
}) {
  return /* @__PURE__ */ jsx(
    Flex,
    {
      borderTopColor: "greyscale.400",
      borderTopStyle: "solid",
      borderTopWidth: "1px",
      direction: "column",
      display: "block",
      justifyContent: "space-between",
      p: { base: "20px 16px", sm: "20px 56px", xl: "20px 32px" },
      w: "100%",
      children: /* @__PURE__ */ jsxs(
        Flex,
        {
          justifyContent: { base: "flex-end", md: "space-between" },
          width: "100%",
          children: [
            /* @__PURE__ */ jsx(Flex, { align: "flex-start", display: { base: "none", md: "flex" }, mr: "-8", children: isSkeletonShown ? /* @__PURE__ */ jsx(
              SkeletonText,
              {
                noOfLines: 1,
                skeletonHeight: 5,
                w: 150 * links.length
              }
            ) : links.map(({ href, title }) => {
              return href && /* @__PURE__ */ jsx(FooterLink, { link: href, title }, href);
            }) }),
            /* @__PURE__ */ jsxs(
              HStack,
              {
                alignItems: "center",
                justifyContent: "end",
                spacing: { base: 4, lg: 6, xl: 8, "2xl": 12 },
                children: [
                  /* @__PURE__ */ jsx(Skeleton, { isLoaded: typeof trustpilotStars === "number", children: /* @__PURE__ */ jsx(
                    chakra.a,
                    {
                      display: { base: "none", md: "block" },
                      flexShrink: 0,
                      target: "_blank",
                      rel: "noopener noreferrer nofollow",
                      href: "https://www.trustpilot.com/review/www.airhelp.com?utm_medium=trustbox&utm_source=MicroCombo",
                      children: /* @__PURE__ */ jsx(
                        TrustpilotStars,
                        {
                          "data-testid": "trustpilot",
                          starLabelText: trustpilotText,
                          numberOfStars: trustpilotStars ?? 4.5
                        }
                      )
                    }
                  ) }),
                  /* @__PURE__ */ jsx(Box, { color: "greyscale.600", fontSize: "sm", textAlign: "right", children: copyright })
                ]
              }
            )
          ]
        }
      )
    }
  );
}
function FooterLink({ link, title }) {
  return /* @__PURE__ */ jsx(Box, { mr: 8, children: /* @__PURE__ */ jsx(
    Link,
    {
      _hover: { color: "greyscale.600" },
      _visited: { color: "font.primary.900" },
      color: "font.primary.900",
      fontSize: 14,
      fontWeight: "normal",
      href: link,
      isExternal: true,
      p: "3 0",
      w: "auto",
      children: title
    }
  ) });
}
function FunnelStepContainer(props) {
  return /* @__PURE__ */ jsx(VStack, { alignItems: "stretch", gap: 4, ...props });
}
const defaultButtonStyles = {
  p: "16px 24px",
  fontSize: "lg",
  minH: "60px",
  fontWeight: "500",
  minW: { base: "100%", md: "170px" }
};
function FunnelStepControlsWrapper({ children, ...props }) {
  return /* @__PURE__ */ jsx(
    Flex,
    {
      alignItems: "flex-end",
      flexWrap: "wrap-reverse",
      gap: "0px 16px",
      justifyContent: "end",
      mt: "2",
      ...props,
      children
    }
  );
}
function FunnelStepBackControl({
  children,
  onClick: onClickProp,
  ...props
}) {
  const funnel = useFunnel();
  function onClick(event) {
    if (onClickProp) {
      onClickProp(event);
    } else {
      funnel.stepBack();
    }
  }
  return /* @__PURE__ */ jsx(
    Button,
    {
      ...defaultButtonStyles,
      onClick,
      variant: "link",
      ...props,
      children
    }
  );
}
function FunnelStepForwardControl({
  children,
  onClick: onClickProp,
  ...props
}) {
  const funnel = useFunnel();
  function onClick(event) {
    if (onClickProp) {
      onClickProp(event);
    } else {
      funnel.stepForward();
    }
  }
  return /* @__PURE__ */ jsx(
    Button,
    {
      "data-testid": "continueButton",
      mb: "32px",
      onClick,
      ...defaultButtonStyles,
      ...props,
      children
    }
  );
}
function FunnelStepControlsTrustpilotBadge({
  variant,
  text,
  score
}) {
  const wrapperProps = {
    display: { base: "flex", md: "none" },
    mb: 8,
    mx: "auto"
  };
  const trustpilotProps = {
    numberOfStars: score,
    starLabelText: text,
    sx: { "@media(width<375px)": { p: { fontSize: "13px" } } },
    minW: defaultButtonStyles.minW
  };
  if (variant === "skeleton") {
    return /* @__PURE__ */ jsx(Skeleton, { ...wrapperProps, children: /* @__PURE__ */ jsx(TrustpilotStars, { ...trustpilotProps }) });
  }
  return /* @__PURE__ */ jsx(TrustpilotStars, { ...wrapperProps, ...trustpilotProps });
}
function FunnelStepSection({
  title,
  titleStyle,
  children,
  tooltip,
  ...props
}) {
  return /* @__PURE__ */ jsxs(
    Box,
    {
      bg: "greyscale.300",
      borderRadius: "lg",
      borderTopLeftRadius: "0",
      p: {
        base: 4,
        sm: 8
      },
      ...props,
      children: [
        title ? /* @__PURE__ */ jsxs(
          Text,
          {
            fontSize: "lg",
            fontWeight: "medium",
            lineHeight: "base",
            mb: children ? 4 : 0,
            ...titleStyle,
            children: [
              title,
              Boolean(tooltip) && /* @__PURE__ */ jsxs(
                Popover,
                {
                  placement: "top",
                  trigger: "hover",
                  closeOnBlur: true,
                  openDelay: 100,
                  children: [
                    /* @__PURE__ */ jsx(PopoverTrigger, { children: /* @__PURE__ */ jsx(
                      Info,
                      {
                        color: "greyscale.400",
                        cursor: "pointer",
                        fontSize: "lg",
                        ml: 1
                      }
                    ) }),
                    /* @__PURE__ */ jsxs(PopoverContent, { children: [
                      Boolean(tooltip == null ? void 0 : tooltip.header) && /* @__PURE__ */ jsx(PopoverHeader, { children: tooltip == null ? void 0 : tooltip.header }),
                      /* @__PURE__ */ jsx(PopoverBody, { children: tooltip == null ? void 0 : tooltip.content })
                    ] })
                  ]
                }
              )
            ]
          }
        ) : null,
        children
      ]
    }
  );
}
function FunnelSettingsModal({
  sections,
  locale,
  currency,
  onItemClick
}) {
  const {
    isOpen,
    onOpen: openSettingsModal,
    onClose: closeSettingModal
  } = useDisclosure();
  const lists = Object.values(sections).map((section) => {
    return {
      listHeading: section.title,
      listItems: section.items.map((item) => {
        return {
          id: item.code,
          name: item.displayName,
          icon: item.icon
        };
      }),
      listAutoflow: section.gridParams
    };
  });
  return /* @__PURE__ */ jsx(
    LanguageSwitcherModal,
    {
      activeItems: [locale, currency],
      flagKey: mapLocaleToFlagKey(locale),
      heading: "",
      isOpen,
      lists,
      onClose: closeSettingModal,
      onItemClick: (item) => {
        onItemClick(item.id);
        closeSettingModal();
      },
      onOpen: openSettingsModal
    }
  );
}
const mapLocaleToFlagKey = (locale) => {
  const localeToFlagKeyMap = {
    da: "dk",
    de: "de",
    en: "gb",
    es: "es",
    el: "gr",
    fr: "fr",
    it: "it",
    nl: "nl",
    nb: "no",
    pl: "pl",
    "pt-BR": "br",
    pt: "pt",
    ru: "ru",
    ro: "ro",
    fi: "fi",
    sv: "se",
    ar: "sa",
    "zh-CN": "cn",
    tr: "tr"
  };
  return localeToFlagKeyMap[locale];
};
function FunnelErrorSection({
  i18n,
  onActionClick,
  actionLink,
  isAuthenticated,
  illustration,
  headingSx,
  textSx
}) {
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs(ErrorSectionWrapper, { children: [
      /* @__PURE__ */ jsx(
        ErrorIllustration,
        {
          pngSrc: illustration == null ? void 0 : illustration.pngSrc,
          webpSrc: illustration == null ? void 0 : illustration.webpSrc
        }
      ),
      /* @__PURE__ */ jsx(Heading, { "data-testid": "errorTitle", mb: 3, variant: "h2", sx: headingSx, children: i18n.title }),
      /* @__PURE__ */ jsx(Text, { fontWeight: 500, mb: 6, sx: textSx, children: i18n.subtitle }),
      /* @__PURE__ */ jsx(
        Button,
        {
          as: "a",
          border: "none",
          "data-testid": "errorLink",
          href: actionLink,
          name: "Error action",
          onClick: onActionClick,
          size: "l",
          children: i18n.actionText
        }
      )
    ] }),
    isAuthenticated ? /* @__PURE__ */ jsx(
      Text,
      {
        as: "p",
        color: "greyscale.700",
        dangerouslySetInnerHTML: {
          __html: i18n.contactUs
        },
        fontSize: "md",
        fontWeight: 500,
        my: 8,
        sx: { "& a": { m: 0, color: "primary.500" } },
        textAlign: "center"
      }
    ) : null
  ] });
}
function ErrorSectionWrapper({ children }) {
  return /* @__PURE__ */ jsx(
    VStack,
    {
      flex: 1,
      justifyContent: "center",
      margin: "0 auto",
      maxW: 700,
      textAlign: "center",
      children
    }
  );
}
function ErrorIllustration({
  webpSrc = "/images/spot/Error.webp",
  pngSrc = "/images/spot/Error.png"
}) {
  return /* @__PURE__ */ jsx(Box, { mb: 4, minH: 256, mt: -10, children: /* @__PURE__ */ jsxs("picture", { children: [
    /* @__PURE__ */ jsx("source", { srcSet: webpSrc, type: "image/webp" }),
    /* @__PURE__ */ jsx(
      "img",
      {
        alt: "AirHelps' character astonished with plane taking off without him",
        src: pngSrc
      }
    )
  ] }) });
}
function FunnelErrorSectionSkeleton({
  actionText,
  illustration
}) {
  return /* @__PURE__ */ jsx(ErrorSectionWrapper, { children: /* @__PURE__ */ jsxs(VStack, { alignItems: "center", children: [
    /* @__PURE__ */ jsx(
      ErrorIllustration,
      {
        pngSrc: illustration == null ? void 0 : illustration.pngSrc,
        webpSrc: illustration == null ? void 0 : illustration.webpSrc
      }
    ),
    /* @__PURE__ */ jsx(
      SkeletonText,
      {
        maxW: "100%",
        mb: 3,
        noOfLines: 1,
        skeletonHeight: 10,
        w: 600
      }
    ),
    /* @__PURE__ */ jsx(
      SkeletonText,
      {
        maxW: "100%",
        mb: 6,
        noOfLines: 1,
        skeletonHeight: 5,
        w: 470
      }
    ),
    /* @__PURE__ */ jsx(Button, { disabled: true, size: "l", children: actionText })
  ] }) });
}
function Form({
  methods,
  onSubmit,
  renderFormTag = false,
  ...formElementProps
}) {
  const onSubmitHandler = (e) => {
    if (onSubmit) {
      e.preventDefault();
      void onSubmit();
    }
  };
  return /* @__PURE__ */ jsx(FormProvider, { ...methods, children: renderFormTag ? /* @__PURE__ */ jsx(
    "form",
    {
      "data-testid": "form-component",
      onSubmit: onSubmitHandler,
      ...formElementProps
    }
  ) : formElementProps.children });
}
function FormInput({
  label,
  placeholder,
  caption,
  name,
  rules,
  shouldUnregister,
  defaultValue,
  control,
  ...inputProps
}) {
  const {
    field,
    fieldState: { invalid, error }
  } = useController({
    name,
    rules,
    shouldUnregister,
    defaultValue,
    control
  });
  return /* @__PURE__ */ jsx(
    Input,
    {
      ...field,
      caption,
      errorMessage: error == null ? void 0 : error.message,
      isInvalid: invalid,
      label,
      placeholder,
      ...inputProps
    }
  );
}
function FormCheckbox({
  children,
  name,
  rules,
  shouldUnregister,
  control,
  ...props
}) {
  const { field, fieldState } = useController({
    name,
    rules,
    shouldUnregister,
    control
  });
  return /* @__PURE__ */ jsx(
    Checkbox,
    {
      alignItems: "flex-start",
      isChecked: field.value,
      isInvalid: fieldState.invalid,
      ...field,
      ...props,
      children
    }
  );
}
function FormCheckboxGroup({ children, ...props }) {
  return /* @__PURE__ */ jsx(Flex, { flexDir: "column", gap: 3, ...props, children });
}
function FormRadioGroup({
  name,
  rules,
  shouldUnregister,
  control,
  options,
  variant = "stacked",
  refine,
  ...props
}) {
  const { field, fieldState } = useController({
    name,
    rules,
    shouldUnregister,
    control
  });
  const optionsStackDirection = {
    base: "column",
    md: variant === "stacked" ? "column" : "row"
  };
  const value = useMemo(() => {
    if (refine) {
      return refine.deserialize(field.value);
    }
    return field.value;
  }, [field.value, refine]);
  const onChange = (fieldValue) => {
    const emittedValue = refine ? refine.serialize(fieldValue) : fieldValue;
    field.onChange(emittedValue);
  };
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(RadioGroup, { name, onChange, value, ...props, children: /* @__PURE__ */ jsx(Stack, { direction: optionsStackDirection, children: options.map((option) => {
      return /* @__PURE__ */ jsx(
        Radio,
        {
          disabled: field.disabled,
          flex: 1,
          isInvalid: fieldState.invalid ? value === option.value : void 0,
          onBlur: field.onBlur,
          value: option.value,
          children: option.label
        },
        option.label
      );
    }) }) }),
    fieldState.error ? /* @__PURE__ */ jsx(Text, { fontSize: "sm", color: "danger.700", children: fieldState.error.message }) : null
  ] });
}
function FormSelect({
  name,
  rules,
  shouldUnregister,
  control,
  options,
  refine,
  disabled,
  ...props
}) {
  var _a;
  const { field, fieldState } = useController({
    name,
    rules,
    shouldUnregister,
    control
  });
  const value = useMemo(() => {
    const finalValue = refine ? refine.deserialize(field.value) : field.value;
    return options.find((option) => option.value === finalValue);
  }, [field.value, refine, options]);
  const onChange = (option) => {
    if (!option) {
      field.onChange(null);
    } else {
      const emittedValue = refine ? refine.serialize(option.value) : option.value;
      field.onChange(emittedValue);
    }
  };
  return /* @__PURE__ */ jsx(
    Select,
    {
      errorMessage: (_a = fieldState.error) == null ? void 0 : _a.message,
      handleChange: onChange,
      isDisabled: disabled,
      isInvalid: fieldState.invalid,
      options,
      value,
      ...props
    }
  );
}
const REVALIDATE_FORMS_EVENT_NAME = "refresh_forms";
const REVALIDATE_FORMS_EVENT = new CustomEvent(
  REVALIDATE_FORMS_EVENT_NAME
);
function triggerGlobalFormRevalidation() {
  window.dispatchEvent(REVALIDATE_FORMS_EVENT);
}
function useForm({
  schema,
  ...useFormProps
}) {
  const form = useForm$1({
    resolver: schema ? zodResolver(schema) : void 0,
    mode: "onChange",
    reValidateMode: "onChange",
    delayError: 500,
    ...useFormProps
  });
  const {
    trigger,
    formState: { errors }
  } = form;
  useEffect(() => {
    function revalidateInvalidFields() {
      Object.keys(errors).forEach((name) => {
        void trigger(name);
      });
    }
    window.addEventListener(
      REVALIDATE_FORMS_EVENT_NAME,
      revalidateInvalidFields
    );
    return () => {
      window.removeEventListener(
        REVALIDATE_FORMS_EVENT_NAME,
        revalidateInvalidFields
      );
    };
  }, [trigger, errors]);
  return form;
}
function useFormHandlers({
  onSubmit,
  // eslint-disable-next-line @typescript-eslint/no-empty-function -- pass a noop as a default value
  onInvalidSubmit = () => {
  }
}) {
  return {
    onSubmit,
    onInvalidSubmit
  };
}
function AuthCardWrapper({
  children,
  ...props
}) {
  return /* @__PURE__ */ jsx(Flex, { alignItems: "center", flexDir: "column", ...props, children });
}
function AuthCard({ children }) {
  return /* @__PURE__ */ jsx(
    Box,
    {
      bg: "white",
      borderRadius: "xl",
      maxW: 384,
      padding: "8",
      shadow: "md",
      w: "full",
      children
    }
  );
}
function AuthCardTitle({
  children,
  ...props
}) {
  return /* @__PURE__ */ jsx(Heading, { as: "h1", fontSize: "2xl", ...props, children });
}
function AuthCardSubtitle({
  children,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    Heading,
    {
      as: "h3",
      fontSize: "lg",
      fontWeight: "semibold",
      mt: 2,
      textAlign: "center",
      ...props,
      children
    }
  );
}
function AuthCardDescription({
  children,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    Text,
    {
      fontSize: "sm",
      fontWeight: "normal",
      color: "greyscale.600",
      mt: 6,
      ...props,
      children
    }
  );
}
function AuthCardAction({ children, ...props }) {
  return /* @__PURE__ */ jsx(Button, { type: "submit", w: "full", ...props, children });
}
function AuthCardDivider({ children }) {
  return /* @__PURE__ */ jsxs(Flex, { alignItems: "center", my: 6, children: [
    /* @__PURE__ */ jsx(Divider, { flex: 1 }),
    /* @__PURE__ */ jsx(Text, { color: "greyscale.600", fontSize: "sm", mx: 4, children }),
    /* @__PURE__ */ jsx(Divider, { flex: 1 })
  ] });
}
function AuthCardCallout({ children }) {
  return /* @__PURE__ */ jsx(Text, { align: "center", fontSize: "sm", mt: 6, children });
}
function AuthCardErrorBox({ text }) {
  const isString = typeof text === "string";
  return /* @__PURE__ */ jsx(
    InfoBox,
    {
      icon: /* @__PURE__ */ jsx(Info, { boxSize: 5, color: "danger.500" }),
      isChat: true,
      mb: 6,
      variant: "danger",
      w: "full",
      children: isString ? /* @__PURE__ */ jsx(Caption, { dangerouslySetInnerHTML: { __html: text } }) : text
    }
  );
}
function AuthCardOAuth({ children }) {
  return /* @__PURE__ */ jsx(Flex, { gap: 1, justifyContent: "center", w: "full", children });
}
function AuthCardGoogleOAuth(props) {
  return /* @__PURE__ */ jsx(OAuthButton, { "aria-label": "Google login provider icon", ...props, children: /* @__PURE__ */ jsxs(
    "svg",
    {
      fill: "none",
      height: "24",
      viewBox: "0 0 24 24",
      width: "24",
      xmlns: "http://www.w3.org/2000/svg",
      children: [
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M22.8933 12.25C22.8933 11.47 22.8233 10.72 22.6933 10H12.3333V14.255H18.2533C17.9983 15.63 17.2233 16.795 16.0583 17.575V20.335H19.6133C21.6933 18.42 22.8933 15.6 22.8933 12.25Z",
            fill: "#4285F4"
          }
        ),
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M12.3332 23C15.3032 23 17.7932 22.015 19.6132 20.335L16.0582 17.575C15.0732 18.235 13.8132 18.625 12.3332 18.625C9.46817 18.625 7.04317 16.69 6.17817 14.09H2.50317V16.94C4.31317 20.535 8.03317 23 12.3332 23Z",
            fill: "#34A853"
          }
        ),
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M6.17825 14.09C5.95825 13.43 5.83325 12.725 5.83325 12C5.83325 11.275 5.95825 10.57 6.17825 9.91V7.06H2.50325C1.73325 8.59286 1.33257 10.2846 1.33325 12C1.33325 13.775 1.75825 15.455 2.50325 16.94L6.17825 14.09Z",
            fill: "#FBBC05"
          }
        ),
        /* @__PURE__ */ jsx(
          "path",
          {
            d: "M12.3332 5.375C13.9482 5.375 15.3982 5.93 16.5382 7.02L19.6932 3.865C17.7882 2.09 15.2982 1 12.3332 1C8.03317 1 4.31317 3.465 2.50317 7.06L6.17817 9.91C7.04317 7.31 9.46817 5.375 12.3332 5.375Z",
            fill: "#EA4335"
          }
        )
      ]
    }
  ) });
}
function AuthCardMicrosoftOAuth(props) {
  return /* @__PURE__ */ jsx(OAuthButton, { "aria-label": "Microsoft login provider icon", ...props, children: /* @__PURE__ */ jsxs(
    "svg",
    {
      fill: "none",
      height: "24",
      viewBox: "0 0 24 24",
      width: "24",
      xmlns: "http://www.w3.org/2000/svg",
      children: [
        /* @__PURE__ */ jsx(
          "path",
          {
            clipRule: "evenodd",
            d: "M0.00170898 11.4087H11.4084V0.00201416H0.00170898V11.4087Z",
            fill: "#F25022",
            fillRule: "evenodd"
          }
        ),
        /* @__PURE__ */ jsx(
          "path",
          {
            clipRule: "evenodd",
            d: "M12.5933 11.4067H23.9999V0H12.5933V11.4067Z",
            fill: "#7FBA00",
            fillRule: "evenodd"
          }
        ),
        /* @__PURE__ */ jsx(
          "path",
          {
            clipRule: "evenodd",
            d: "M0 24H11.4067V12.5933H0V24Z",
            fill: "#00A4EF",
            fillRule: "evenodd"
          }
        ),
        /* @__PURE__ */ jsx(
          "path",
          {
            clipRule: "evenodd",
            d: "M12.5933 24H23.9999V12.5933H12.5933V24Z",
            fill: "#FFB900",
            fillRule: "evenodd"
          }
        )
      ]
    }
  ) });
}
function AuthCardFacebookOAuth(props) {
  return /* @__PURE__ */ jsx(OAuthButton, { "aria-label": "Facebook login provider icon", ...props, children: /* @__PURE__ */ jsx(
    "svg",
    {
      fill: "none",
      height: "24",
      viewBox: "0 0 24 24",
      width: "24",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M10.3917 20.6L10.3665 12.6H7.1665V9.4H10.3665V7.4C10.3665 4.43088 12.2052 3 14.8538 3C16.1225 3 17.213 3.09446 17.5307 3.13668V6.23958L15.6938 6.24042C14.2533 6.24042 13.9744 6.92492 13.9744 7.92937V9.4H18.1665L16.5665 12.6H13.9744V20.6H10.3917Z",
          fill: "#0D3073"
        }
      )
    }
  ) });
}
function AuthCardAppleOAuth(props) {
  return /* @__PURE__ */ jsx(OAuthButton, { "aria-label": "Auth login provider icon", ...props, children: /* @__PURE__ */ jsx(
    "svg",
    {
      width: "24",
      height: "24",
      viewBox: "0 0 24 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M20.9005 8.2702C20.763 8.37572 18.334 9.72882 18.334 12.7375C18.334 16.2176 21.4247 17.4488 21.5173 17.4793C21.503 17.5543 21.0263 19.1654 19.8877 20.8069C18.8725 22.2515 17.8122 23.6937 16.1993 23.6937C14.5863 23.6937 14.1712 22.7674 12.3092 22.7674C10.4946 22.7674 9.84945 23.7242 8.37408 23.7242C6.8987 23.7242 5.86926 22.3875 4.68564 20.7459C3.31463 18.8183 2.20691 15.8236 2.20691 12.9814C2.20691 8.42262 5.2051 6.00487 8.15585 6.00487C9.72374 6.00487 11.0307 7.02262 12.0151 7.02262C12.952 7.02262 14.4132 5.94389 16.1969 5.94389C16.8729 5.94389 19.3018 6.00487 20.9005 8.2702ZM15.3501 4.01391C16.0878 3.14858 16.6096 1.94791 16.6096 0.747236C16.6096 0.580737 16.5954 0.411893 16.5645 0.275879C15.3643 0.320435 13.9364 1.06616 13.0754 2.05344C12.3993 2.81324 11.7684 4.01391 11.7684 5.231C11.7684 5.41391 11.7992 5.59682 11.8135 5.65545C11.8894 5.66952 12.0127 5.68594 12.136 5.68594C13.2129 5.68594 14.5673 4.97304 15.3501 4.01391Z",
          fill: "black"
        }
      )
    }
  ) });
}
function OAuthButton({ children, ...props }) {
  return /* @__PURE__ */ jsx(
    Link,
    {
      _hover: { background: "greyscale.200" },
      backgroundColor: "greyscale.100",
      borderRadius: "sm",
      px: { base: 6, md: 8 },
      py: 3,
      ...props,
      children
    }
  );
}
function AuthFormOAuth({
  googleLink,
  microsoftLink,
  facebookLink,
  appleLink,
  onGoogleClick,
  onMicrosoftClick,
  onFacebookClick,
  onAppleClick,
  ...props
}) {
  return /* @__PURE__ */ jsxs(AuthCardOAuth, { ...props, children: [
    /* @__PURE__ */ jsx(
      AuthCardGoogleOAuth,
      {
        "data-testid": "oauth-google",
        href: googleLink,
        onClick: onGoogleClick
      }
    ),
    /* @__PURE__ */ jsx(
      AuthCardMicrosoftOAuth,
      {
        "data-testid": "oauth-microsoft",
        href: microsoftLink,
        onClick: onMicrosoftClick
      }
    ),
    /* @__PURE__ */ jsx(
      AuthCardFacebookOAuth,
      {
        "data-testid": "oauth-facebook",
        href: facebookLink,
        onClick: onFacebookClick
      }
    ),
    /* @__PURE__ */ jsx(
      AuthCardAppleOAuth,
      {
        "data-testid": "oauth-apple",
        href: appleLink,
        onClick: onAppleClick
      }
    )
  ] });
}
function AuthAuthenticatedSection({
  text,
  actionText,
  signoutText,
  onActionClick,
  onSignoutClick,
  isSignoutPending,
  ...props
}) {
  return /* @__PURE__ */ jsx(Box, { maxW: 720, ...props, children: /* @__PURE__ */ jsxs(VStack, { alignItems: "stretch", gap: 4, children: [
    /* @__PURE__ */ jsx(FunnelStepSection, { "data-testid": "logged-in-message", title: text }),
    /* @__PURE__ */ jsx(Button, { onClick: onActionClick, children: actionText }),
    /* @__PURE__ */ jsx(
      Button,
      {
        "data-testid": "sign-out-button",
        isLoading: isSignoutPending,
        onClick: onSignoutClick,
        variant: "secondary",
        children: signoutText
      }
    )
  ] }) });
}
const passwordChangeSchema = z.object({
  password: z.string().min(8),
  passwordConfirmation: z.string().min(8)
}).refine((data) => data.password === data.passwordConfirmation, {
  params: { i18n: "passwords_do_not_match" },
  path: ["passwordConfirmation"]
});
function AuthPasswordChangeForm({
  i18n,
  onPasswordChange,
  isPasswordChangePending,
  errorText
}) {
  const form = useForm({
    schema: passwordChangeSchema,
    defaultValues: {
      password: "",
      passwordConfirmation: ""
    }
  });
  function onSubmit() {
    void form.handleSubmit(({ password }) => {
      onPasswordChange(password);
    })();
  }
  return /* @__PURE__ */ jsxs(VStack, { alignItems: "stretch", minW: 320, children: [
    /* @__PURE__ */ jsx(AuthCardTitle, { children: i18n.header }),
    /* @__PURE__ */ jsx(Form, { methods: form, onSubmit, renderFormTag: true, children: /* @__PURE__ */ jsxs(
      VStack,
      {
        css: { '& [role="group"] label': { display: "none" } },
        gap: 6,
        my: 6,
        children: [
          /* @__PURE__ */ jsx(
            FormInput,
            {
              "data-testid": "password",
              name: "password",
              placeholder: i18n.passwordPlaceholder,
              type: "password"
            }
          ),
          /* @__PURE__ */ jsx(
            FormInput,
            {
              "data-testid": "password-confirmation",
              name: "passwordConfirmation",
              placeholder: i18n.confirmPasswordPlaceholder,
              type: "password"
            }
          ),
          errorText ? /* @__PURE__ */ jsx(AuthCardErrorBox, { text: errorText }) : null,
          /* @__PURE__ */ jsx(
            AuthCardAction,
            {
              "data-testid": "reset-password-action-button",
              isLoading: isPasswordChangePending,
              children: i18n.actionText
            }
          )
        ]
      }
    ) }),
    /* @__PURE__ */ jsx(AuthCardCallout, { children: i18n.callout })
  ] });
}
const signinSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1)
});
function AuthSigninForm({
  onSubmit: onSubmitProp,
  onInvalidSubmit,
  formError,
  isSubmitting,
  i18n,
  oAuth,
  isFullWidth,
  isEmailDisabled,
  headersAlign = "center",
  ...props
}) {
  const form = useForm({
    schema: signinSchema,
    defaultValues: {
      email: "",
      password: ""
    }
  });
  function onSubmit() {
    void form.handleSubmit(onSubmitProp, onInvalidSubmit)();
  }
  return /* @__PURE__ */ jsxs(Flex, { flexDir: "column", maxW: isFullWidth ? "100%" : 384, w: "full", children: [
    /* @__PURE__ */ jsx(AuthCardTitle, { textAlign: headersAlign, children: i18n.title }),
    i18n.subtitle ? /* @__PURE__ */ jsx(AuthCardSubtitle, { textAlign: headersAlign, children: i18n.subtitle }) : void 0,
    /* @__PURE__ */ jsxs(Form, { methods: form, onSubmit, renderFormTag: true, children: [
      /* @__PURE__ */ jsxs(Flex, { flexDir: "column", gap: 6, my: 6, ...props, children: [
        /* @__PURE__ */ jsx(
          FormInput,
          {
            "data-testid": "email",
            disabled: isEmailDisabled,
            name: "email",
            placeholder: i18n.emailPlaceholder
          }
        ),
        /* @__PURE__ */ jsx(
          FormInput,
          {
            "data-testid": "password",
            name: "password",
            placeholder: i18n.passwordPlaceholder,
            type: "password"
          }
        )
      ] }),
      formError ? /* @__PURE__ */ jsx(AuthCardErrorBox, { text: formError }) : null,
      /* @__PURE__ */ jsx(AuthCardAction, { "data-testid": "action-submit", isLoading: isSubmitting, children: i18n.actionText })
    ] }),
    /* @__PURE__ */ jsx(AuthCardCallout, { children: i18n.forgotYourPassword }),
    oAuth ? /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(AuthCardDivider, { children: i18n.divider }),
      oAuth
    ] }) : null,
    /* @__PURE__ */ jsx(AuthCardCallout, { children: i18n.signinCallout })
  ] });
}
const generateRandomPassword = () => {
  const randomValue = Math.random().toString(36).slice(2) + Date.now().toString();
  return randomValue.toString().slice(5, 25);
};
const signupSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8),
  passwordConfirmation: z.string().min(8),
  privacyConsent: z.literal(true),
  marketingConsent: z.boolean()
}).refine((data) => data.password === data.passwordConfirmation, {
  params: { i18n: "passwords_do_not_match" },
  path: ["passwordConfirmation"]
});
function AuthSignupForm({
  onSubmit: onSubmitProp,
  onInvalidSubmit,
  formError,
  isSubmitting,
  i18n,
  oAuth,
  defaultValues,
  isFullWidth,
  isEmailDisabled,
  headersAlign = "center",
  withPassword = true,
  ...props
}) {
  const randomPassword = generateRandomPassword();
  const form = useForm({
    schema: signupSchema,
    defaultValues: {
      email: "",
      password: withPassword ? "" : randomPassword,
      passwordConfirmation: withPassword ? "" : randomPassword,
      privacyConsent: false,
      marketingConsent: false,
      ...defaultValues
    }
  });
  const [isSelectAllIndeterminate, setIsSelectAllIndeterminate] = useState(false);
  const onSelectAllChange = (e) => {
    form.setValue("privacyConsent", e.target.checked);
    form.setValue("marketingConsent", e.target.checked);
    setIsSelectAllIndeterminate(false);
  };
  useEffect(() => {
    const subscription = form.watch((values) => {
      if (values.marketingConsent === true || values.privacyConsent === true) {
        setIsSelectAllIndeterminate(true);
      } else {
        setIsSelectAllIndeterminate(false);
      }
    });
    return () => {
      subscription.unsubscribe();
    };
  }, [form]);
  function onSubmit() {
    void form.handleSubmit(onSubmitProp, onInvalidSubmit)();
  }
  return /* @__PURE__ */ jsxs(Flex, { flexDir: "column", maxW: isFullWidth ? "100%" : 384, w: "full", children: [
    /* @__PURE__ */ jsx(AuthCardTitle, { textAlign: headersAlign, children: i18n.title }),
    i18n.subtitle ? /* @__PURE__ */ jsx(AuthCardSubtitle, { textAlign: headersAlign, children: i18n.subtitle }) : void 0,
    /* @__PURE__ */ jsxs(Form, { methods: form, onSubmit, renderFormTag: true, children: [
      /* @__PURE__ */ jsxs(Flex, { flexDir: "column", gap: 6, my: 6, ...props, children: [
        /* @__PURE__ */ jsx(
          FormInput,
          {
            "data-testid": "email",
            disabled: isEmailDisabled,
            name: "email",
            placeholder: i18n.emailPlaceholder
          }
        ),
        withPassword ? /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(
            FormInput,
            {
              "data-testid": "password",
              name: "password",
              placeholder: i18n.passwordPlaceholder,
              type: "password"
            }
          ),
          /* @__PURE__ */ jsx(
            FormInput,
            {
              "data-testid": "password-confirmation",
              name: "passwordConfirmation",
              placeholder: i18n.confirmPasswordPlaceholder,
              type: "password"
            }
          )
        ] }) : void 0,
        /* @__PURE__ */ jsxs(FormCheckboxGroup, { children: [
          /* @__PURE__ */ jsx(
            Checkbox,
            {
              "data-testid": "select-all-consent",
              isIndeterminate: isSelectAllIndeterminate,
              onChange: onSelectAllChange,
              children: /* @__PURE__ */ jsx(Caption, { color: "greyscale.600", children: i18n.selectAllLabel })
            }
          ),
          /* @__PURE__ */ jsx(
            FormCheckbox,
            {
              "data-testid": "agree-tc-and-privacy-consent",
              name: "privacyConsent",
              children: /* @__PURE__ */ jsx(Caption, { color: "greyscale.600", children: i18n.privacyConsentLabel })
            }
          ),
          /* @__PURE__ */ jsx(
            FormCheckbox,
            {
              "data-testid": "marketing-consent",
              name: "marketingConsent",
              children: /* @__PURE__ */ jsx(Caption, { color: "greyscale.600", children: i18n.marketingConsentLabel })
            }
          )
        ] })
      ] }),
      formError ? /* @__PURE__ */ jsx(AuthCardErrorBox, { text: formError }) : null,
      /* @__PURE__ */ jsx(AuthCardAction, { "data-testid": "signup-action", isLoading: isSubmitting, children: i18n.actionText })
    ] }),
    oAuth ? /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(AuthCardDivider, { children: i18n.divider }),
      oAuth
    ] }) : null,
    /* @__PURE__ */ jsx(AuthCardCallout, { children: i18n.signinCallout })
  ] });
}
function AuthSummarySection({
  i18n,
  onActionClick,
  headersAlign = "center",
  action = /* @__PURE__ */ jsx(AuthCardAction, { "data-testid": "auth-page-link", onClick: onActionClick, children: i18n.actionText })
}) {
  return /* @__PURE__ */ jsxs(VStack, { alignItems: "stretch", minW: 320, children: [
    /* @__PURE__ */ jsx(Box, { mx: "auto", children: /* @__PURE__ */ jsx(SuccessIllustration, {}) }),
    /* @__PURE__ */ jsx(AuthCardTitle, { textAlign: headersAlign, children: i18n.heading }),
    /* @__PURE__ */ jsx(AuthCardDescription, { mb: 6, children: i18n.description }),
    action
  ] });
}
function SuccessIllustration() {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      fill: "none",
      height: "96",
      viewBox: "0 0 97 96",
      width: "97",
      xmlns: "http://www.w3.org/2000/svg",
      children: [
        /* @__PURE__ */ jsx("circle", { cx: "48.2662", cy: "47.766", fill: "#DDF7EE", r: "36.766" }),
        /* @__PURE__ */ jsx(
          "path",
          {
            clipRule: "evenodd",
            d: "M59.8743 41.0605C59.0412 40.269 57.7281 40.2889 56.9193 41.1052L46.7118 51.4078L41.6258 46.136C40.8259 45.3068 39.5072 45.2769 38.6705 46.069C37.8126 46.881 37.7929 48.2404 38.6267 49.077L45.6551 56.1288C46.2391 56.7147 47.1871 56.7173 47.7742 56.1345L59.9075 44.0906C60.7525 43.2518 60.7375 41.8806 59.8743 41.0605Z",
            fill: "#00E4A9",
            fillRule: "evenodd"
          }
        )
      ]
    }
  );
}
function AuthPasswordResetEmailSection({
  i18n,
  errorText,
  onPasswordReset,
  isPending,
  headersAlign = "center"
}) {
  const form = useForm({
    schema: z.object({ email: z.string().email() }),
    defaultValues: { email: "" }
  });
  function onSubmit() {
    void form.handleSubmit((data) => {
      onPasswordReset(data.email);
    })();
  }
  return /* @__PURE__ */ jsxs(VStack, { alignItems: "stretch", minW: 320, children: [
    /* @__PURE__ */ jsx(AuthCardTitle, { textAlign: headersAlign, children: i18n.introHeader }),
    i18n.subtitle ? /* @__PURE__ */ jsx(AuthCardDescription, { children: i18n.subtitle }) : null,
    /* @__PURE__ */ jsxs(Form, { methods: form, onSubmit, renderFormTag: true, children: [
      /* @__PURE__ */ jsx(
        FormInput,
        {
          "data-testid": "email-input",
          name: "email",
          placeholder: i18n.emailPlaceholder
        }
      ),
      errorText ? /* @__PURE__ */ jsx(AuthCardErrorBox, { text: errorText }) : null,
      /* @__PURE__ */ jsx(
        AuthCardAction,
        {
          mt: 6,
          "data-testid": "reset-password-action-button",
          isLoading: isPending,
          children: i18n.resetPasswordAction
        }
      )
    ] }),
    i18n.callout ? /* @__PURE__ */ jsx(AuthCardCallout, { children: i18n.callout }) : null
  ] });
}
function FunnelSummarySection({
  title,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs(VStack, { alignItems: "stretch", maxW: 700, spacing: 5, w: "full", ...props, children: [
    /* @__PURE__ */ jsx(Heading, { fontSize: { base: "36px", lg: "5xl" }, variant: "h2", children: title }),
    children
  ] });
}
function FunnelSummarySectionDescription(props) {
  return /* @__PURE__ */ jsx(VStack, { alignItems: "start", spacing: { base: 5, lg: 4 }, ...props });
}
function FunnelSummarySectionDescriptionLine(props) {
  return /* @__PURE__ */ jsx(Caption, { fontSize: "md", ...props });
}
function FunnelSummarySectionBookmark({
  link,
  beforeCopyText,
  afterCopyText
}) {
  return /* @__PURE__ */ jsx(Box, { maxW: 540, children: /* @__PURE__ */ jsx(
    LinkGenerator,
    {
      afterCopyText,
      beforeCopyText,
      link
    }
  ) });
}
function FunnelSummarySectionInstructions({
  title,
  description,
  items,
  footer
}) {
  return /* @__PURE__ */ jsxs(
    VStack,
    {
      align: "stretch",
      backgroundColor: "greyscale.300",
      borderRadius: "lg",
      maxW: 700,
      mt: 6,
      my: 2,
      p: 8,
      spacing: 6,
      children: [
        /* @__PURE__ */ jsxs(Stack, { children: [
          /* @__PURE__ */ jsx(Text, { color: "primary.900", fontSize: "lg", fontWeight: 700, children: title }),
          Boolean(description) && /* @__PURE__ */ jsx(Text, { color: "primary.900", fontSize: "md", mb: 2, children: description })
        ] }),
        items.map(
          ({ title: itemTitle, description: itemDescription, icon: Icon }, index) => (
            // eslint-disable-next-line react/no-array-index-key -- does not change order
            /* @__PURE__ */ jsxs(Flex, { children: [
              /* @__PURE__ */ jsx(Icon, { boxSize: 5, color: "primary.500", mr: 3, mt: "2px" }),
              /* @__PURE__ */ jsxs(VStack, { align: "stretch", spacing: 1, children: [
                /* @__PURE__ */ jsx(Text, { color: "primary.900", fontSize: "md", fontWeight: 500, children: itemTitle }),
                /* @__PURE__ */ jsx(Text, { fontSize: "md", fontWeight: 400, children: itemDescription })
              ] })
            ] }, index)
          )
        ),
        Boolean(footer) && /* @__PURE__ */ jsx(Box, { mt: 2, children: footer })
      ]
    }
  );
}
function FunnelSummarySectionNotice({ children }) {
  return /* @__PURE__ */ jsx(Note, { children });
}
const defaultFunnelStyles = {
  components: {
    Link: {
      baseStyle: {
        _focus: {
          boxShadow: "none"
        }
      }
    },
    Button: {
      baseStyle: {
        border: "none",
        _focus: {
          boxShadow: "none"
        }
      }
    },
    Modal: {
      baseStyle: {
        CloseButton: {
          _focus: {
            outline: "none"
          }
        }
      }
    }
  }
};
const defaultFunnelTheme = extendTheme(theme, defaultFunnelStyles);
export {
  AirhelpLogo,
  AuthAuthenticatedSection,
  AuthCard,
  AuthCardAction,
  AuthCardCallout,
  AuthCardDivider,
  AuthCardErrorBox,
  AuthCardFacebookOAuth,
  AuthCardGoogleOAuth,
  AuthCardMicrosoftOAuth,
  AuthCardOAuth,
  AuthCardTitle,
  AuthCardWrapper,
  AuthFormOAuth,
  AuthPasswordChangeForm,
  AuthPasswordResetEmailSection,
  AuthSigninForm,
  AuthSignupForm,
  AuthSummarySection,
  Caption,
  ErrorNote,
  Form,
  FormCheckbox,
  FormCheckboxGroup,
  FormInput,
  FormRadioGroup,
  FormSelect,
  FunnelAlert,
  FunnelAlertPortal,
  FunnelAvatar,
  FunnelContent,
  F as FunnelContextProvider,
  FunnelErrorSection,
  FunnelErrorSectionSkeleton,
  FunnelFooter,
  FunnelHeader,
  FunnelProgressBar,
  FunnelSettingsModal,
  FunnelShell,
  FunnelSidebar,
  FunnelStepBackControl,
  FunnelStepContainer,
  FunnelStepControlsTrustpilotBadge,
  FunnelStepControlsWrapper,
  FunnelStepForwardControl,
  FunnelStepSection,
  FunnelSummarySection,
  FunnelSummarySectionBookmark,
  FunnelSummarySectionDescription,
  FunnelSummarySectionDescriptionLine,
  FunnelSummarySectionInstructions,
  FunnelSummarySectionNotice,
  Note,
  REVALIDATE_FORMS_EVENT,
  REVALIDATE_FORMS_EVENT_NAME,
  R as ReactFunnel,
  S as StaticFunnel,
  branch,
  buildSteps,
  defaultFunnelTheme,
  group,
  split,
  step,
  stepTemplate,
  triggerGlobalFormRevalidation,
  useForm,
  useFormHandlers,
  useFunnel,
  useFunnelAlertRef
};
//# sourceMappingURL=index.js.map
